/**
 * File System Access API Service
 * Allows direct read/write access to local files and folders
 * https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
 */

import indexedDBService from './indexedDBService';

export interface FileSystemHandle {
  kind: 'file' | 'directory';
  name: string;
  handle: FileSystemFileHandle | FileSystemDirectoryHandle;
}

export interface OpenedFolder {
  name: string;
  handle: FileSystemDirectoryHandle;
  files: Map<string, FileSystemFileHandle>;
}

class FileSystemAccessService {
  private openedFolder: OpenedFolder | null = null;

  /**
   * Check if File System Access API is supported
   */
  isSupported(): boolean {
    return 'showDirectoryPicker' in window;
  }

  /**
   * Open a local folder with read/write permissions
   */
  async openFolder(): Promise<OpenedFolder | null> {
    if (!this.isSupported()) {
      throw new Error('File System Access API is not supported in this browser');
    }

    try {
      // @ts-ignore - showDirectoryPicker is not in standard types yet
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
      });

      this.openedFolder = {
        name: dirHandle.name,
        handle: dirHandle,
        files: new Map(),
      };

      // Scan all files in the directory
      await this.scanDirectory(dirHandle);

      return this.openedFolder;
    } catch (error) {
      if ((error as any).name === 'AbortError') {
        // User cancelled the dialog
        return null;
      }
      console.error('Failed to open folder:', error);
      throw error;
    }
  }

  /**
   * Scan directory recursively and store file handles
   */
  private async scanDirectory(dirHandle: FileSystemDirectoryHandle, path = ''): Promise<void> {
    if (!this.openedFolder) return;

    // @ts-ignore - for await is supported for async iterables
    for await (const entry of dirHandle.values()) {
      const entryPath = path ? `${path}/${entry.name}` : entry.name;

      if (entry.kind === 'file') {
        this.openedFolder.files.set(entryPath, entry as FileSystemFileHandle);
      } else if (entry.kind === 'directory') {
        await this.scanDirectory(entry as FileSystemDirectoryHandle, entryPath);
      }
    }
  }

  /**
   * Get the currently opened folder
   */
  getOpenedFolder(): OpenedFolder | null {
    return this.openedFolder;
  }

  /**
   * Read file content from local drive
   */
  async readFile(path: string): Promise<string> {
    if (!this.openedFolder) {
      throw new Error('No folder is opened');
    }

    const fileHandle = this.openedFolder.files.get(path);
    if (!fileHandle) {
      throw new Error(`File not found: ${path}`);
    }

    const file = await fileHandle.getFile();
    return await file.text();
  }

  /**
   * Write content to a local file
   */
  async writeFile(path: string, content: string): Promise<void> {
    if (!this.openedFolder) {
      throw new Error('No folder is opened');
    }

    // Get or create the file handle
    let fileHandle = this.openedFolder.files.get(path);
    
    if (!fileHandle) {
      // Create new file
      const parts = path.split('/');
      const fileName = parts.pop()!;
      let currentDir = this.openedFolder.handle;
      
      // Create parent directories if needed
      for (const part of parts) {
        currentDir = await currentDir.getDirectoryHandle(part, { create: true });
      }
      
      fileHandle = await currentDir.getFileHandle(fileName, { create: true });
      this.openedFolder.files.set(path, fileHandle);
    }

    // Write to file
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
  }

  /**
   * Create a new file in the opened folder
   */
  async createFile(path: string, content = ''): Promise<void> {
    if (!this.openedFolder) {
      throw new Error('No folder is opened');
    }

    const parts = path.split('/');
    const fileName = parts.pop()!;
    let currentDir = this.openedFolder.handle;

    // Create parent directories if needed
    for (const part of parts) {
      if (part) {
        currentDir = await currentDir.getDirectoryHandle(part, { create: true });
      }
    }

    const fileHandle = await currentDir.getFileHandle(fileName, { create: true });
    this.openedFolder.files.set(path, fileHandle);

    // Write initial content if provided
    if (content) {
      await this.writeFile(path, content);
    }
  }

  /**
   * Delete a file from the opened folder
   */
  async deleteFile(path: string): Promise<boolean> {
    if (!this.openedFolder) {
      throw new Error('No folder is opened');
    }

    const fileHandle = this.openedFolder.files.get(path);
    if (fileHandle) {
      // @ts-ignore - remove is available in some browsers
      await fileHandle.remove?.();
      this.openedFolder.files.delete(path);
      return true;
    }
    return false;
  }

  /**
   * List all files in the opened folder
   */
  listFiles(): string[] {
    if (!this.openedFolder) return [];
    return Array.from(this.openedFolder.files.keys());
  }

  /**
   * Close the opened folder
   */
  closeFolder(): void {
    this.openedFolder = null;
  }

  /**
   * Upload file via input dialog (fallback for unsupported browsers)
   */
  async uploadFile(): Promise<{ name: string; content: string } | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.txt,.js,.ts,.json,.md,.py,.html,.css'; // Allowed file types for code/models
      input.onchange = async (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('File size exceeds 10MB limit');
        }

        // Validate file type
        const allowedTypes = ['text/plain', 'application/json', 'application/javascript', 'text/javascript', 'text/typescript', 'text/markdown', 'text/html', 'text/css', 'application/x-python'];
        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(txt|js|ts|json|md|py|html|css)$/)) {
          throw new Error('Unsupported file type');
        }

        const content = await file.text();
        resolve({ name: file.name, content });
      };
      input.click();
    });
  }

  /**
   * Download file to user's downloads
   */
  downloadFile(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Store model file in IndexedDB (for local models)
   */
  async storeModelFile(modelId: string, file: File): Promise<void> {
    // Validate
    if (file.size > 100 * 1024 * 1024) { // 100MB for models
      throw new Error('Model file size exceeds 100MB limit');
    }

    const content = await file.arrayBuffer();
    // Store in IndexedDB via the service
    const mawiDB = indexedDBService;
    await mawiDB.llm.setModelFile(modelId, content, {
      name: file.name,
      type: 'local-model',
      size: file.size,
      uploadedAt: new Date().toISOString(),
    });
  }

  /**
   * Retrieve model file from IndexedDB
   */
  async getModelFile(modelId: string): Promise<ArrayBuffer | null> {
    const mawiDB = indexedDBService;
    const model = await mawiDB.llm.getModelFile(modelId);
    return model ? model.data : null;
  }
}

export const fileSystemAccess = new FileSystemAccessService();
export default fileSystemAccess;